import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ContactForm.module.css';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Divider
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { DataProviderInstance } from '../../../api/DataProvider';
import { SUPPORT_EMAIL, WHATSAPP_SUPPORT_DISPLAY, getWhatsAppSupportLink } from '../../../consts/ContactConfig';
import { FeedbackConfirmation, Toast } from '../../../components/support';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../components/buttons/CustomButton';

function ContactForm() {
  const navigate = useNavigate();
  const { Contact } = DataProviderInstance();
  
  const [formData, setFormData] = useState({
    title: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'O título é obrigatório';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'A mensagem é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData object for contact submission
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.message);
      submitData.append('currentRoute', currentRoute);

      // Call API to submit the contact form using the new endpoint
      await Contact(submitData, (response) => {
        if (response.success) {
          setSubmitSuccess(true);
          setToast({
            open: true,
            message: 'Mensagem enviada com sucesso!',
            severity: 'success'
          });
        } else {
          // Show error toast
          setToast({
            open: true,
            message: response.error || 'Ocorreu um erro ao enviar o formulário. Tente novamente.',
            severity: 'error'
          });
          setErrors({ submit: response.error || 'Ocorreu um erro ao enviar o formulário. Tente novamente.' });
        }
        setIsSubmitting(false);
      });
    } catch (error) {
      // Show error toast
      setToast({
        open: true,
        message: 'Ocorreu um erro ao enviar o formulário. Tente novamente.',
        severity: 'error'
      });
      setErrors({ submit: 'Ocorreu um erro ao enviar o formulário. Tente novamente.' });
      setIsSubmitting(false);
    }
  };

  // Handle toast close
  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast({ ...toast, open: false });
  };

  if (submitSuccess) {
    return (
      <FeedbackConfirmation
        title="Mensagem enviada com sucesso!"
        message="Agradecemos por entrar em contato. Nossa equipe irá analisar sua mensagem e responder o mais breve possível."
        buttonText="Voltar para o Dashboard"
        returnPath="/dashboard"
      />
    );
  }

  return (
    <div className='page-content'>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <AreaTitle
          Title="Entrar em Contato"
          Icon={<EmailOutlinedIcon />}
          Description="Utilize este formulário para entrar em contato com nossa equipe de suporte. Responderemos o mais breve possível."
        />
        
        <Box sx={{ mt: 1, mb: 2, display: 'flex', alignItems: 'center' }}>
          <EmailOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'var(--color-primary)' }} />
          <Typography variant="body1">
            Email de contato: <strong>{SUPPORT_EMAIL}</strong>
          </Typography>
        </Box>

        <Box sx={{ mt: 1, mb: 2, display: 'flex', alignItems: 'center' }}>
          <WhatsAppIcon fontSize="small" sx={{ mr: 1, color: 'var(--color-primary)' }} />
          <Typography variant="body1">
            WhatsApp: <a href={getWhatsAppSupportLink()} target="_blank" rel="noopener noreferrer"><strong>{WHATSAPP_SUPPORT_DISPLAY}</strong></a>
          </Typography>
        </Box>

        <Divider sx={{ marginTop: '16px', marginBottom: '24px' }} />
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup} style={{ marginBottom: '24px' }}>
            <TextField
              label="Título"
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              placeholder="Ex: Dúvida sobre agendamento"
              required
              error={!!errors.title}
              helperText={errors.title}
            />
          </div>

          <div className={styles.formGroup} style={{ marginBottom: '32px' }}>
            <TextField
              label="Mensagem"
              name="message"
              value={formData.message}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              placeholder="Digite sua mensagem aqui..."
              required
              error={!!errors.message}
              helperText={errors.message}
            />
          </div>

          {errors.submit && (
            <Box mt={2} mb={2}>
              <Typography color="error">{errors.submit}</Typography>
            </Box>
          )}

          {/* Toast notification */}
          <Toast
            open={toast.open}
            onClose={handleToastClose}
            message={toast.message}
            severity={toast.severity}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CustomButton
              type="submit"
              variant="contained"
              style="primary"
              label={isSubmitting ? <CircularProgress size={24} /> : 'Enviar Mensagem'}
              disabled={isSubmitting}
              className={styles.submitButton}
            />
          </div>
        </form>
      </Paper>
    </div>
  );
}

export default ContactForm;